import { useRef, type ReactNode } from 'react'
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
	AiDraftBenefitAnimation,
	CentralizedInboxBenefitAnimation,
	CustomerContextBenefitAnimation,
	CustomizeBenefitAnimation,
	EmbedBenefitAnimation,
	MentionsBenefitAnimation,
} from '@/components/landing/BenefitAnimations'

function BenefitRow({ children, reverse }: { children: ReactNode; reverse: boolean }) {
 const target = useRef<HTMLDivElement>(null)
 const reduced = useReducedMotion()
 const desktop = useMediaQuery('(min-width: 1024px)')
 const { scrollYProgress } = useScroll({ target, offset: ['start end', 'end start'] })
 const shift = useTransform(scrollYProgress, [0, 1], ['55px', '-55px'])
 return <motion.div ref={target} style={desktop && !reduced ? { '--benefit-shift': shift } as import('framer-motion').MotionStyle : undefined}
  className={`fb-benefit-row flex flex-col lg:flex-row items-center gap-12 lg:gap-16 mb-20 lg:mb-32 last:mb-0 ${reverse ? 'lg:flex-row-reverse' : ''}`}>{children}</motion.div>
}

export function FeaturesAlternatingAnimated() {
	const { t, i18n } = useTranslation()

	const features = [
		{
			title: t('featuresAlternating.centralized.title'),
			description: t('featuresAlternating.centralized.description'),
			cta: t('featuresAlternating.centralized.cta'),
			visualPosition: 'right' as const,
			visual: <CentralizedInboxBenefitAnimation />,
		},
		{
			title: t('featuresAlternating.feature0.title'),
			description: t('featuresAlternating.feature0.description'),
			cta: t('featuresAlternating.feature0.cta'),
			planBadge: t('featuresAlternating.feature0.planBadge'),
			visualPosition: 'right' as const,
			visual: <AiDraftBenefitAnimation />,
		},
		{
			title: t('featuresAlternating.feature1.title'),
			description: t('featuresAlternating.feature1.description'),
			cta: t('featuresAlternating.feature1.cta'),
			visualPosition: 'right' as const,
			visual: <CustomerContextBenefitAnimation />,
		},
		{
			title: t('featuresAlternating.feature5.title'),
			description: t('featuresAlternating.feature5.description'),
			cta: t('featuresAlternating.feature5.cta'),
			planBadge: t('featuresAlternating.feature5.planBadge'),
			visualPosition: 'right' as const,
			visual: <MentionsBenefitAnimation />,
		},
		{
			title: t('featuresAlternating.embed.title'),
			description: t('featuresAlternating.embed.description'),
			points: [
				t('featuresAlternating.anim.embed.inline'),
				t('featuresAlternating.anim.embed.floating'),
				t('featuresAlternating.anim.embed.trigger'),
			],
			visualPosition: 'right' as const,
			visual: <EmbedBenefitAnimation />,
		},
		{
			title: t('featuresAlternating.customize.title'),
			description: t('featuresAlternating.customize.description'),
			visualPosition: 'left' as const,
			visual: <CustomizeBenefitAnimation />,
		},
	]

	return (
		<section id="benefits-animated" className="fb-benefits py-16 lg:py-20">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-16 lg:mb-20">
					<h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">
						{t('featuresAlternating.title')}
					</h2>
				</div>
				{features.map((feature) => (
					<BenefitRow key={feature.title} reverse={feature.visualPosition === 'left'}>
						<div className="fb-benefit-copy flex-1 space-y-6">
							<h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">
								{feature.title}
							</h2>
							{feature.planBadge && (
								<Badge variant="secondary" className="text-sm">
									{feature.planBadge}
								</Badge>
							)}
							<p className="text-lg text-gray-600 leading-relaxed max-w-xl">
								{feature.description}
							</p>
							{feature.points && (
								<ul className="space-y-2">
									{feature.points.map((point) => (
										<li
											key={point}
											className="flex items-center gap-2 text-base font-medium text-gray-800"
										>
											<Check className="h-4 w-4 text-primary shrink-0" />
											{point}
										</li>
									))}
								</ul>
							)}
							{feature.cta && (
								<div className="pt-4">
									<Link to="/auth">
										<Button
											size="lg"
											className="gap-2 font-semibold bg-primary hover:bg-primary/90 text-white px-6 py-3 text-base"
										>
											{feature.cta.replace(/\s*→$/, '')}
											<ArrowRight className="w-4 h-4" />
										</Button>
									</Link>
								</div>
							)}
						</div>
						<div className="fb-benefit-visual flex-1 w-full">
							
							{feature.visual}</div>
					</BenefitRow>
				))}
			</div>
		</section>
	)
}
